import { useState } from 'react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Loader2, Building2, HardHat, GraduationCap, ClipboardCheck, Layers, AlertCircle } from 'lucide-react';
import { useSetIndustry } from '@/hooks/use-industry';
import type { Industry } from '@/lib/types';

const ORG_TYPES: { key: Industry; label: string; description: string; icon: React.ReactNode }[] = [
  {
    key: 'field_ops',
    label: 'Rope Access / Field Operations',
    description: 'IRATA/SPRAT teams working on external job sites with mobile crews and equipment.',
    icon: <HardHat className="h-8 w-8" />,
  },
  {
    key: 'facility_ops',
    label: 'Internal Facility Operations',
    description: 'In-house maintenance teams managing equipment and safety within fixed buildings.',
    icon: <Building2 className="h-8 w-8" />,
  },
  {
    key: 'training_ops',
    label: 'Training Provider',
    description: 'IRATA/SPRAT training centres running courses and managing candidates at their own venues.',
    icon: <GraduationCap className="h-8 w-8" />,
  },
  {
    key: 'consultant',
    label: 'Consultant / Assessor',
    description: 'Specialising in documents, audits and assessments across client sites.',
    icon: <ClipboardCheck className="h-8 w-8" />,
  },
  {
    key: 'hybrid',
    label: 'Hybrid Operations',
    description: 'Combines field, facility and consulting capabilities — all features enabled.',
    icon: <Layers className="h-8 w-8" />,
  },
];

interface OrgTypeStepProps {
  /** Org type already chosen when the org was created, if any. */
  currentOrgType?: Industry | null;
  onComplete: () => void;
}

export function OrgTypeStep({ currentOrgType = null, onComplete }: OrgTypeStepProps) {
  const [selected, setSelected] = useState<Industry | null>(currentOrgType);
  const [isChanging, setIsChanging] = useState(currentOrgType == null);
  const [error, setError] = useState<string | null>(null);
  const setIndustry = useSetIndustry();

  const visibleTypes = isChanging
    ? ORG_TYPES
    : ORG_TYPES.filter((opt) => opt.key === selected);

  const handleContinue = () => {
    if (!selected) return;
    if (selected === currentOrgType) {
      onComplete();
      return;
    }
    setIndustry.mutate(selected, {
      onSuccess: () => onComplete(),
      onError: (err) => {
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to save organization type. Please try again.';
        setError(msg);
      },
    });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
          <Building2 className="h-8 w-8 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          {isChanging ? 'What type of organization are you?' : 'Your organization type'}
        </h1>
        <p className="mt-2 text-gray-500">
          {isChanging
            ? 'This helps us tailor the platform to your operations.'
            : 'This was set when your organization was created. Change it if it doesn\u2019t fit.'}
        </p>
      </div>

      <div
        className="space-y-4 mb-8"
        data-required="true"
        data-required-empty={selected ? 'false' : 'true'}
        data-required-message="Select an organization type."
        tabIndex={-1}
      >
        {visibleTypes.map((opt) => {
          const isSelected = selected === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => { setError(null); setSelected(opt.key); }}
              className={`w-full text-left rounded-lg border-2 p-5 transition-colors ${
                isSelected
                  ? 'border-blue-600 bg-blue-50/50'
                  : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
              }`}
            >
              <Card className="border-0 shadow-none bg-transparent">
                <CardContent className="flex items-start gap-4 p-0">
                  <div
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg ${
                      isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {opt.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{opt.label}</h3>
                    <p className="mt-1 text-sm text-gray-500">{opt.description}</p>
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {!isChanging && (
          <Button type="button" variant="outline" onClick={() => setIsChanging(true)}>
            Change
          </Button>
        )}
        <Button
          onClick={handleContinue}
          data-validate-required="true"
          disabled={setIndustry.isPending}
        >
          {setIndustry.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Continue
        </Button>
      </div>
    </div>
  );
}
